import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, Send } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supportService } from '../../services/supportService';
import { TICKET_STATUS_BADGE } from '../../types/support';

export default function SupportTicketDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [replyBody, setReplyBody] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  const ticketId = Number(id);

  const { data, isLoading, error } = useQuery({
    queryKey: ['support-ticket', ticketId],
    queryFn: () => supportService.getTicket(ticketId),
    enabled: !isNaN(ticketId),
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data?.messages]);

  const mutation = useMutation({
    mutationFn: () => supportService.addMessage(ticketId, replyBody.trim()),
    onSuccess: () => {
      setReplyBody('');
      queryClient.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 text-teal-400 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="flex items-center gap-2 text-red-400 bg-red-900/20 p-4 rounded-lg">
          <AlertCircle className="w-4 h-4" />
          Failed to load ticket
        </div>
      </div>
    );
  }

  const { ticket, messages } = data;
  const isClosed = ticket.status === 'closed';

  return (
    <div className="p-6 max-w-3xl mx-auto flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/support/tickets')}
          className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-white font-semibold truncate">{ticket.subject}</h1>
          <p className="text-slate-500 text-xs mt-0.5">
            Opened {new Date(ticket.created_at).toLocaleString()}
          </p>
        </div>
        <span
          className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full ${TICKET_STATUS_BADGE[ticket.status] ?? TICKET_STATUS_BADGE.open}`}
        >
          {ticket.status.replace('_', ' ')}
        </span>
      </div>

      {/* Message thread */}
      <div className="space-y-3">
        {messages.map((msg) => {
          const isOwn = !msg.is_admin;
          return (
            <div key={msg.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[80%] rounded-xl px-4 py-3 text-sm ${
                  isOwn
                    ? 'bg-teal-700/40 text-teal-100'
                    : 'bg-slate-700 text-slate-200'
                }`}
              >
                {msg.is_admin && (
                  <p className="text-xs text-slate-400 mb-1 font-medium">Support</p>
                )}
                <p className="whitespace-pre-wrap break-words">{msg.body}</p>
                <p className={`text-xs mt-1 ${isOwn ? 'text-teal-400' : 'text-slate-500'}`}>
                  {new Date(msg.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Reply form */}
      {!isClosed && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          {mutation.isError && (
            <div className="flex items-center gap-2 text-red-400 text-sm mb-3">
              <AlertCircle className="w-4 h-4" />
              Failed to send reply
            </div>
          )}
          <textarea
            className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 resize-none"
            rows={3}
            placeholder="Write a reply..."
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.ctrlKey && replyBody.trim()) {
                e.preventDefault();
                mutation.mutate();
              }
            }}
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={() => mutation.mutate()}
              disabled={!replyBody.trim() || mutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
            >
              {mutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Send
            </button>
          </div>
        </div>
      )}

      {isClosed && (
        <p className="text-center text-sm text-slate-500">This ticket is closed.</p>
      )}
    </div>
  );
}
